/**
 * Processing activity — compact trigger + responsive drawer (right desktop, bottom mobile).
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, Timeline } from "@mui/icons-material";
import useDeviceType from "../hooks/useDeviceType.js";
import { getLeadOpsTimeline } from "../services/leadOpsTimelineService.js";
import { summarizeLeadOpsActivity } from "../utils/leadOpsActivitySummary.js";
import LeadOpsTimelinePanel from "./LeadOpsTimelinePanel.jsx";

/**
 * @param {object} props
 * @param {string} props.leadId
 */
const LeadOpsActivitySection = ({ leadId }) => {
  const { isMobile } = useDeviceType();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activitySummary, setActivitySummary] = useState(
    summarizeLeadOpsActivity([])
  );

  const loadSummary = useCallback(async () => {
    if (!leadId) {
      return;
    }

    setSummaryLoading(true);
    try {
      const result = await getLeadOpsTimeline(leadId, {
        sort: "desc",
        limit: 50,
      });
      const events = result?.success ? result.data?.events || [] : [];
      setActivitySummary(summarizeLeadOpsActivity(events));
    } catch {
      setActivitySummary(summarizeLeadOpsActivity([]));
    } finally {
      setSummaryLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    loadSummary();
  };

  if (!leadId) {
    return null;
  }

  return (
    <>
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent
          sx={{
            py: 2,
            "&:last-child": { pb: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Processing activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auto-pricing, enrichment, and quote preparation
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Chip
              label={summaryLoading ? "Loading…" : activitySummary.label}
              color={activitySummary.color}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Timeline />}
              onClick={() => setDrawerOpen(true)}
            >
              View activity
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: isMobile
            ? {
                maxHeight: "85vh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }
            : {
                width: { xs: "100%", sm: 480 },
              },
        }}
      >
        <Box
          sx={{
            height: isMobile ? "85vh" : "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.25 }}>
                Processing activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lead submission, auto-pricing, enrichment, and quote prep
              </Typography>
            </Box>
            <IconButton
              onClick={handleDrawerClose}
              aria-label="Close processing activity"
              edge="end"
            >
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <LeadOpsTimelinePanel leadId={leadId} embedded />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default LeadOpsActivitySection;
